import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, push } from 'react-router-redux';
import { SubmissionError, reset, getFormValues, change } from 'redux-form';
import cloneDeep from 'lodash/cloneDeep';
import orderBy from 'lodash/orderBy';
import forEach from 'lodash/forEach';
import find from 'lodash/find';
import get from 'lodash/get';
import isNull from 'lodash/isNull';
import isEqual from 'lodash/isEqual';

import http from '../../shared/http';
import endpoints from '../../../config/endpoints';
import translation from '../../../config/translation';

import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';

import { enum as affiliateLevelsEnum } from '../../../../../../enums/affiliate-levels';

import { actions as rootActions } from '../../root';
import { create as createPolling } from '../../../../../shared/js/intervals';
import createCloseForm from '../components/close-form/for-thread-item';
import createCannedReplyForm from '../components/canned-reply-form';
import { prepareTicketsList } from '../../shared/thread-utils';

const isRocket = process.env.CPA_ENV === 'rocket';

const threadPolling = createPolling('thread', {
	newMessages: { interval: 5000 }
});

const closeForm = createCloseForm({
	namespace: 'threadsItem',
	stateNodeName: 'closeThreadForm'
});

const cannedReplyForm = createCannedReplyForm({
	namespace: 'threadsItem',
	stateNodeName: 'cannedReplyForm'
});

// actions types
const FETCH_DATA_SUCCESS = 'threads-item/fetch-data-success';
const FETCH_ANSWERS_SUCCESS = 'threads-item/fetch-answers-success';
const FETCH_AFFILIATE_SUCCESS = 'threads-item/fetch-affiliate-success';
const FETCH_AFFILIATE_ERROR = 'threads-item/fetch-affiliate-error';

const LOGIN_WITH_AFFILIATE_SUCCESS = 'threads-item/login-with-affiliate-success';
const CHANGE_AFFILIATE_LEVEL = 'threads-item/change-affiliate-level';
const SUBMIT_ANSWER_SUCCESS = 'threads-item/submit-answer-success';
const SUBMIT_ANSWER_ERROR = 'threads-item/submit-answer-error';
const PREPARATION_FILE_SUCCESS = 'threads-item/preparation-file-success';
const UPLOAD_SUCCESS = 'threads-item/upload-success';
const REQUEST_ERROR = 'threads-item/request-error';

const TOGGLE_PROGRESS_BAR = 'threads-item/toggle-progress-bar';
const REMOVE_FILE = 'threads-item/remove-file';
const OFFER_ACCESS_SUCCESS = 'threads-item/offer-access-success';
const TICKET_CLOSE_SUCCESS = 'threads-item/ticket-close-success';

const PAUSE_SUCCESS = 'threads-item/pause-success';
const UNPAUSE_SUCCESS = 'threads-item/unpause-success';

const FETCH_SEARCH_THREADS = 'threads-item/fetch-search-threads';
const TOGGLE_EDIT_MODE = 'threads-item/toggle-edit-mode';

const SUBMIT_EDIT_SUCCESS = 'threads-item/submit-edit-success';
const SUBMIT_EDIT_ERROR = 'threads-item/submit-edit-error';
const UPDATE_FILES_TO_UPLOAD = 'threads-item/update-files-to-upload';

const FETCH_TICKET_FOR_DIFF_SUCCESS = 'threads-item/fetch-ticket-for-diff-success';

// permissions
export const allowedPermissions = {
	view: 'threads_list',
	answer: 'threads_answer',
	changeLevel: 'affiliates_update',
	login: 'affiliates_login',
	viewAffiliates: 'affiliates_list',
	viewAnalytics: 'analytics_view',
	closeTicket: 'threads_close',
	closeGiftOrderTicket: 'threads_gift_order_close'
};

// initial state
const initialState = {
	data: null,
	affiliate: null,
	pendingOffers: [],
	filesToUpload: [],
	answerFormInitialValues: {
		text: null,
		files: null
	},
	editPostId: null,
	search: null,
	showProgressBar: false,
	closeThreadForm: closeForm.initialState,
	cannedReplyForm: cannedReplyForm.initialState,
	initialPosts: null,
	isCannedReplyMode: false,
	cannedReplyEditId: null,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: props => dispatch => {
			const ticketListQueryContext = window.location && window.location.search || '';

			const request = http({
				url: [
					`${endpoints.THREADS.LIST}/${props.params.id}`,
					`${endpoints.THREADS.LIST}${ticketListQueryContext}`,
					`${endpoints.THREADS.LIST}/canned-replies`
				],
				method: 'get',
				actions: [FETCH_DATA_SUCCESS, REQUEST_ERROR]
			});

			request(dispatch).then(action => {
				if (action.type === FETCH_DATA_SUCCESS) {
					threadPolling.actions.start(
						'thread',
						'newMessages',
						actions.fetchAnswers({ params: { id: props.params.id } }),
						dispatch
					);
					dispatch(rootActions.fetchUnreadPosts());

					http({
						url: [
							`${endpoints.AFFILIATES.BASE}/${action.payload[0].data['affiliate_id']}`,
							endpoints.AFFILIATES.PENDING_OFFER.replace('{id}', action.payload[0].data['affiliate_id'])
						],
						method: 'get',
						actions: [FETCH_AFFILIATE_SUCCESS, FETCH_AFFILIATE_ERROR]
					})(dispatch);
				}
			});
		},

		fetchAnswers: props => dispatch => {
			const request = http({
				url: `${endpoints.THREADS.LIST}/${props.params.id}`,
				method: 'get',
				actions: [FETCH_ANSWERS_SUCCESS, REQUEST_ERROR]
			});

			request(dispatch).then(action => {
				if (action.type === FETCH_DATA_SUCCESS) {
					threadPolling.actions.start(
						'thread',
						'newMessages',
						actions.fetchAnswers({ params: { id: props.params.id } }),
						dispatch
					);
				}
			});
		},

		loginWithAffiliate: id => dispatch => {
			const isNewDashboard = isRocket && confirm('Перейти в новый дашборд?');

			const pageData = {
				pathname: window.location.pathname,
				title: 'Affiliate ticket'
			};

			const request = http({
				url: endpoints.AFFILIATES.LOGIN.replace('{id}', id),
				method: 'post',
				actions: [LOGIN_WITH_AFFILIATE_SUCCESS, REQUEST_ERROR],
				data: {
					isNewDashboard,
					source_page: pageData
				}
			});
			request(dispatch).then(action => {
				if (action.type === LOGIN_WITH_AFFILIATE_SUCCESS) {
					window.location.replace(action.payload.data.url);
				}
			});
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),

		// @todo after implement internal page progressbar rewrite logic
		switchToTicket: ticketId => dispatch => {
			dispatch(push(`threads/${ticketId}`));
		},

		submitAnswer: data => (dispatch, getState) => {
			const state = getState();
			const userId = state.root.data.id;
			const { data: { ticket: { id } }, initialPosts } = state.threadsItem;
			const confirmText = 'New posts have been submitted to this thread since you opened it, ' +
				'are you sure you want to submit your post?';

			const fetchNewTicketData = http({
				url: `${endpoints.THREADS.LIST}/${id}`,
				method: 'get',
				actions: [FETCH_TICKET_FOR_DIFF_SUCCESS, REQUEST_ERROR]
			});

			const request = http({
				url: `${endpoints.THREADS.LIST}/${id}`,
				method: 'post',
				actions: [SUBMIT_ANSWER_SUCCESS, SUBMIT_ANSWER_ERROR],
				data: {
					text: data.text || ''
				}
			});

			return fetchNewTicketData(dispatch).then(action => {
				if (action.type === FETCH_TICKET_FOR_DIFF_SUCCESS) {
					const { posts } = action.payload.data;
					// diff actual posts and posts from when the thread was opened
					// if something changed, warn user before submitting new post
					const newPostsByOtherUsers = !isEqual(
						initialPosts.filter(p => p.author === 'affiliate' || p.author_id !== userId),
						posts.filter(p => p.author === 'affiliate' || p.author_id !== userId)
					);
					if (newPostsByOtherUsers && !confirm(confirmText)) {
						return;
					}
					return request(dispatch).then(action => {
						if (action.type === SUBMIT_ANSWER_SUCCESS) {
							const postId = action.payload.data.id;
							const { filesToUpload } = getState().threadsItem;

							dispatch(reset('supportForm'));

							if (!filesToUpload) {
								dispatch(actions.fetchData({ params: { id } }));
							} else {
								dispatch(actions.upload(postId)).then(action => {
									if (action.type === UPLOAD_SUCCESS) {
										dispatch(actions.fetchData({ params: { id } }));
									}
								});
							}
						}
						if (action.type === SUBMIT_ANSWER_ERROR && action.payload.response.status === 400) {
							const validationsErrors = {};
							const responseErrors = action.payload.response.data;

							forEach(responseErrors, value => {
								const key = value.dataPath.slice(1);
								validationsErrors[key] = value.message;
							});

							throw new SubmissionError(validationsErrors);
						}
					});
				}
			});
		},

		submitEdit: data => (dispatch, getState) => {
			const state = getState().threadsItem;
			const { ticket } = state.data;
			const { posts, id } = ticket;
			const { editPostId, filesToUpload } = state;

			const postBeforeEdit = find(posts, { id: editPostId });
			const { attachments } = postBeforeEdit;

			const attachmentsRemoved = attachments && attachments.filter(file => !!find(filesToUpload, { name: file.name }));
			const newFilesToUpload = filesToUpload && filesToUpload.filter(file => !find(attachments, { name: file.name }));

			const request = http({
				url: `${endpoints.THREADS.LIST}/${id}/posts/${editPostId}`,
				method: 'put',
				actions: [SUBMIT_EDIT_SUCCESS, SUBMIT_EDIT_ERROR],
				data: {
					text: data.text || '',
					attachments: attachmentsRemoved ? attachmentsRemoved : null
				}
			});

			return request(dispatch).then(action => {
				if (action.type === SUBMIT_EDIT_SUCCESS) {
					dispatch(actions.toggleEditMode());
					dispatch(reset('supportForm'));

					if (!newFilesToUpload.length) {
						dispatch(actions.fetchData({ params: { id } }));
					} else {
						dispatch(actions.updateFilesToUpload(newFilesToUpload));
						dispatch(actions.upload(editPostId)).then(action => {
							if (action.type === UPLOAD_SUCCESS) {
								dispatch(actions.fetchData({ params: { id } }));
							}
						});
					}
				}
				if (action.type === SUBMIT_EDIT_ERROR && action.payload.response.status === 400) {
					const validationsErrors = {};
					const responseErrors = action.payload.response.data;

					forEach(responseErrors, value => {
						const key = value.dataPath.slice(1);
						validationsErrors[key] = value.message;
					});

					throw new SubmissionError(validationsErrors);
				}
			});
		},

		preparationFile: (file, nextAction) => dispatch => {
			nextAction(file.name);

			dispatch({
				type: PREPARATION_FILE_SUCCESS,
				payload: file
			});
		},

		upload: postId => (dispatch, getState) => {
			const data = new FormData();
			const { filesToUpload } = getState().threadsItem;

			filesToUpload.forEach((file, index) => {
				data.append(`files[${index}]`, file);
			});

			dispatch(actions.toggleProgressBar());

			const request = http({
				url: endpoints.THREADS.UPLOAD.replace('{postId}', postId),
				method: 'put',
				actions: [UPLOAD_SUCCESS, REQUEST_ERROR],
				data
			});

			return request(dispatch);
		},

		removeFile: index => ({
			type: REMOVE_FILE,
			payload: index
		}),

		updateFilesToUpload: files => ({
			type: UPDATE_FILES_TO_UPLOAD,
			payload: files
		}),

		submitOfferAccess: data => (dispatch, getState) => {
			const { offers } = data;
			const { affiliate, pendingOffers } = getState().threadsItem;
			const requestUrls = [];
			const mustClosedThreadIds = [];

			offers.forEach((offer, id) => {
				const threadId = get(find(pendingOffers, { offer_id: id }), 'thread_id');
				if (offer) {
					requestUrls.push(endpoints.OFFERS.GRAND_ACCESS.replace('{id}', id));
					mustClosedThreadIds.push(threadId);
				}
			});

			dispatch(actions.toggleProgressBar());

			const request = http({
				url: requestUrls,
				method: 'put',
				actions: [OFFER_ACCESS_SUCCESS, REQUEST_ERROR],
				data: {
					affiliate_id: affiliate.id
				}
			});

			request(dispatch).then(action => {
				if (action.type === OFFER_ACCESS_SUCCESS) {
					dispatch(actions.offerAccessAutoAnswer(true, mustClosedThreadIds)).then(() => {
						dispatch(actions.close(mustClosedThreadIds));
					});
				}
			});
		},

		changeAffiliateLevel: level => (dispatch, getState) => {
			const { affiliate, data: { ticket }, pendingOffers } = getState().threadsItem;

			dispatch(actions.toggleProgressBar());

			const request = http({
				url: `${endpoints.AFFILIATES.BASE}/${affiliate.id}`,
				method: 'put',
				actions: [CHANGE_AFFILIATE_LEVEL, REQUEST_ERROR],
				data: {
					level
				}
			});

			request(dispatch).then(action => {
				if (action.type === CHANGE_AFFILIATE_LEVEL) {
					if (level === affiliateLevelsEnum.average && pendingOffers.length) {
						const submitFormat = [];
						pendingOffers.forEach(entity => {
							submitFormat[entity.offer_id] = true;
						});
						dispatch(actions.submitOfferAccess({ offers: submitFormat }));
					} else {
						dispatch(actions.fetchData({ params: { id: ticket.id } }));
					}
				}
			});
		},

		rejectOfferAccess: () => (dispatch, getState) => {
			const { pendingOffers } = getState().threadsItem;
			const { offers } = getFormValues('ticketMetaForm')(getState());
			const threadIds = [];

			offers.forEach((offer, id) => {
				threadIds.push(get(find(pendingOffers, { offer_id: id }), 'thread_id'));
			});

			dispatch(actions.offerAccessAutoAnswer(false, threadIds)).then(action => {
				dispatch(actions.close(threadIds));
			});
		},

		close: (threadIds = [], closeMessage = '') => (dispatch, getState) => {
			const { data: { ticket } } = getState().threadsItem;
			const url = threadIds.map(id => endpoints.THREADS.CLOSE.replace('{id}', id));

			dispatch(actions.toggleProgressBar());

			const request = http({
				url,
				method: 'put',
				actions: [TICKET_CLOSE_SUCCESS, REQUEST_ERROR],
				data: {
					closeMessage
				}
			});

			request(dispatch).then(action => {
				if (action.type === TICKET_CLOSE_SUCCESS) {
					dispatch(actions.fetchData({ params: { id: ticket.id } }));
				}
			});
		},

		pause: id => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());

			http({
				url: endpoints.THREADS.PAUSE.replace('{id}', id),
				method: 'put',
				actions: [PAUSE_SUCCESS, REQUEST_ERROR]
			})(dispatch);
		},

		unpause: id => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());

			http({
				url: endpoints.THREADS.UNPAUSE.replace('{id}', id),
				method: 'put',
				actions: [UNPAUSE_SUCCESS, REQUEST_ERROR]
			})(dispatch);
		},

		offerAccessAutoAnswer: (isAllowedAccess, threadIds = []) => (dispatch, getState) => {
			const { affiliate: { language }, pendingOffers } = getState().threadsItem;
			const message = get(
				translation[isAllowedAccess ? 'You have access to the offer' : 'You have no access to the offer'],
				language
			);
			const url = [];
			const data = [];

			threadIds.forEach(threadId => {
				const offerTitle = get(find(pendingOffers, { thread_id: threadId }), 'offer');
				url.push(`${endpoints.THREADS.LIST}/${threadId}`);
				data.push({
					text: `${message} "${offerTitle}"`
				});
			});

			return http({
				url,
				method: 'post',
				actions: [SUBMIT_ANSWER_SUCCESS, SUBMIT_ANSWER_ERROR],
				data
			})(dispatch);
		},

		search: () => (dispatch, getState) => {
			const searchForm = getFormValues('threadsItemSearchForm')(getState());
			const search = searchForm && searchForm.search;

			http({
				url: `${endpoints.THREADS.LIST}?search=${search || ''}`,
				method: 'get',
				actions: [FETCH_SEARCH_THREADS, REQUEST_ERROR]
			})(dispatch);
		},

		toggleEditMode: id => ({
			type: TOGGLE_EDIT_MODE,
			payload: id
		}),

		substituteWithCannedReply: text => dispatch => {
			dispatch(change('supportForm', 'text', text));
		}
	},
	closeForm.actions,
	cannedReplyForm.actions
);

// reducers
const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			threadPolling.actions.stop('thread', 'newMessages');
			return initialState;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => {
			const [ticket, ticketsList, cannedReplies] = action.payload.map(entity => entity.data);
			return {
				...state,
				data: {
					ticket,
					ticketsList,
					cannedReplies
				},
				initialPosts: ticket.posts,
				error: false
			};
		},
		[FETCH_ANSWERS_SUCCESS]: (state, action) => {
			const ticket = action.payload.data;
			return {
				...state,
				data: {
					...state.data,
					ticket
				}
			};
		},
		[FETCH_AFFILIATE_SUCCESS]: (state, action) => {
			const [affiliate, pendingOffers] = action.payload.map(entity => entity.data);
			return {
				...state,
				affiliate,
				pendingOffers,
				showProgressBar: false
			};
		},
		[FETCH_AFFILIATE_ERROR]: (state, action) => {
			const { status } = action.payload.response;

			if (status === 403) {
				return {
					...state,
					affiliate: initialState.affiliate,
					pendingOffers: initialState.pendingOffers,
					showProgressBar: false
				};
			}
			return { ...state, error: true, showProgressBar: false };
		},
		[FETCH_SEARCH_THREADS]: (state, action) => {
			const threads = action.payload.data;
			return {
				...state,
				data: {
					...state.data,
					ticketsList: threads
				}
			};
		},
		[PAUSE_SUCCESS]: state => ({ ...state, data: null, showProgressBar: false }),
		[UNPAUSE_SUCCESS]: state => ({ ...state, data: null, showProgressBar: false }),
		[REQUEST_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[PREPARATION_FILE_SUCCESS]: (state, action) => ({
			...state,
			filesToUpload: [].concat(state.filesToUpload, action.payload)
		}),
		[UPLOAD_SUCCESS]: state => ({ ...state, filesToUpload: [] }),
		[REMOVE_FILE]: (state, action) => ({
			...state,
			filesToUpload: state.filesToUpload.filter((file, index) => index !== action.payload)
		}),
		[TOGGLE_EDIT_MODE]: (state, action) => {
			const postId = action.payload;
			const post = state.data && state.data.ticket && find(state.data.ticket.posts, { id: postId });
			return {
				...state,
				answerFormInitialValues: {
					files: null,
					text: post ? post.text : null
				},
				editPostId: postId ? postId : null,
				filesToUpload: post && post.attachments ? post.attachments : []
			};
		},
		[UPDATE_FILES_TO_UPLOAD]: (state, action) => ({
			...state,
			filesToUpload: action.payload
		})
	},
	closeForm.reducers,
	cannedReplyForm.reducers
);
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.threadsItem.data && prepareTicket(state.threadsItem.data['ticket']),
	getTicketsList: state => state.threadsItem.data && prepareTicketsList(state, 'threads'),
	getAffiliate: state => (state.threadsItem.affiliate && state.threadsItem.affiliate) || {},
	getAttachmentsInfo: state =>
		state.threadsItem.filesToUpload && state.threadsItem.filesToUpload.map(file => file.name),
	getAnswerFormInitialValues: state => state.threadsItem.answerFormInitialValues,
	getEditMode: state => !isNull(state.threadsItem.editPostId),
	getIntl: state => intl(state),
	getProgressBar: state => state.threadsItem.showProgressBar,
	getCan: state => checkPermissions(state, allowedPermissions),
	getAffiliateLevelList: state => state.root.lists['affiliate-levels'],
	getThreadStatusesList: state => state.root.lists['thread-statuses'],
	getPendingOfferAccessRequests: state => state.threadsItem.pendingOffers,
	getCloseFormInitialValues: state => closeForm.selectors.getInitialValues(state),
	getCloseFormShouldShowStatus: state => closeForm.selectors.getShouldShowCloseFormStatus(state),
	getCloseFormValidate: state => closeForm.selectors.getCloseFormValidate(state),
	getCannedReply: state => ({
		data: state.threadsItem.data && state.threadsItem.data.cannedReplies,
		formValues: cannedReplyForm.selectors.getFormValues(state),
		shouldShowForm: cannedReplyForm.selectors.getShouldShowForm(state)
	}),
	getError: state => state.threadsItem.error
};

// functions
const prepareTicket = (ticket, adminInfo) => {
	const posts = [].concat(ticket.posts);

	// Added close message as posts
	if (ticket.status === 'closed' && ticket['close_message'] && ticket['close_message'].length) {
		posts.push({
			id: null,
			author: 'admin',
			name: ticket['admin_closed_name'],
			avatar: ticket['admin_closed_avatar'],
			text: ticket['close_message'],
			updated_at: ticket.updated_at,
			isCloseMessage: true
		});
	}

	return {
		...ticket,
		posts: orderBy(posts, 'id', 'asc')
	};
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/threads/item/index.js